/**
 * Floating inline toolbar for the active text layer + colour popover.
 * Renders to `document.body` via createPortal so the canvas's
 * `overflow-hidden` can't clip it when it sits above/below the active layer.
 */
import { useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AlignLeft, AlignCenter, AlignRight, Trash2 } from "lucide-react";
import {
  FONT_FAMILIES, FONT_CSS_MAP, PALETTE_GROUPS,
  readRecentColors, pushRecentColor,
} from "./constants";

export function ColorPopover({ layer, onChange }) {
  const [open, setOpen] = useState(false);
  const [recent, setRecent] = useState(readRecentColors());
  const pick = (c) => {
    onChange(layer.id, { color: c });
    pushRecentColor(c);
    setRecent(readRecentColors());
    setOpen(false);
  };
  return (
    <div className="relative" onPointerDown={(e) => e.stopPropagation()}>
      <button type="button" onClick={() => setOpen((v) => !v)}
              className="h-6 w-6 rounded border border-stone-300 shadow-inner relative"
              style={{ backgroundColor: layer.color }}
              title="Renk" data-testid="pe-tb-color-btn">
        <span className="sr-only">Renk</span>
      </button>
      {open && (
        <div className="absolute z-50 top-7 right-0 w-[280px] bg-white rounded-lg shadow-2xl border border-stone-200 p-2"
             data-testid="pe-tb-color-pop">
          <div className="flex items-center gap-1 mb-2">
            <input type="color" value={layer.color}
                   onChange={(e) => pick(e.target.value)}
                   className="h-7 w-9 border rounded cursor-pointer"
                   data-testid="pe-tb-color-input" />
            <input value={layer.color}
                   onChange={(e) => onChange(layer.id, { color: e.target.value })}
                   onBlur={(e) => pushRecentColor(e.target.value)}
                   className="h-7 flex-1 text-[11px] font-mono px-2 border rounded"
                   data-testid="pe-tb-color-hex" />
          </div>
          {recent.length > 0 && (
            <>
              <div className="text-[9px] font-bold uppercase text-stone-400 mb-1">Son Kullanılan</div>
              <div className="flex flex-wrap gap-1 mb-2">
                {recent.map((c, i) => (
                  <button key={`${c}-${i}`} type="button" onClick={() => pick(c)}
                          className="w-5 h-5 rounded border border-stone-300"
                          style={{ backgroundColor: c }} title={c}
                          data-testid={`pe-tb-color-recent-${i}`} />
                ))}
              </div>
            </>
          )}
          {PALETTE_GROUPS.map(([label, colors]) => (
            <div key={label} className="mb-1.5">
              <div className="text-[9px] font-bold uppercase text-stone-400 mb-0.5">{label}</div>
              <div className="flex flex-wrap gap-1">
                {colors.map((c) => {
                  const sel = layer.color?.toLowerCase() === c.toLowerCase();
                  return (
                    <button key={c} type="button" onClick={() => pick(c)}
                            className={`w-5 h-5 rounded border ${sel ? "ring-2 ring-indigo-600 ring-offset-1" : "border-stone-300"}`}
                            style={{ backgroundColor: c }}
                            title={c}
                            data-testid={`pe-tb-sw-${c.slice(1)}`} />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function InlineToolbar({ layer, allLayers = [], onChange, onDelete, canvasRef }) {
  // Toolbar portal ile body'e taşınır; böylece canvas'ın overflow-hidden'ı
  // kesmez ve toolbar canvas'ın üst/alt kenarının dışına taşarak komşu
  // katmanları ÖRTMEDEN yerleşebilir.
  const [rect, setRect] = useState(null);
  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const update = () => setRect(canvas.getBoundingClientRect());
    update();
    const ro = new ResizeObserver(update);
    ro.observe(canvas);
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [canvasRef]);

  if (!layer || !rect) return null;
  const TBW = 460;
  const TBH = 40;
  const GAP = 8;
  const estTextH = Math.max(28, layer.size * rect.height * 1.2);

  // Yatay konum: katmanın anchor noktasına göre.
  let leftLocal = layer.x * rect.width;
  if (layer.align === "center") leftLocal -= TBW / 2;
  else if (layer.align === "right") leftLocal -= TBW;
  leftLocal = Math.max(6, Math.min(rect.width - TBW - 6, leftLocal));

  const yTopLocal = layer.y * rect.height;
  const yBotLocal = yTopLocal + estTextH;

  const tbL = leftLocal;
  const tbR = leftLocal + TBW;
  const neighbors = allLayers
    .filter((l) => l.id !== layer.id && !l.hidden && (l.text || "").trim())
    .map((l) => {
      let nL = l.x * rect.width;
      const nW = Math.max(40, (l.max_width || 0.6) * rect.width);
      if (l.align === "center") nL -= nW / 2;
      else if (l.align === "right") nL -= nW;
      const nT = l.y * rect.height;
      const nB = nT + Math.max(28, l.size * rect.height * 1.2);
      return { nL, nR: nL + nW, nT, nB };
    })
    .filter((n) => !(n.nR < tbL || n.nL > tbR));

  const spaceAboveLayer = yTopLocal;
  const spaceBelowLayer = rect.height - yBotLocal;

  const nbAboveBottom = Math.max(0, ...neighbors.filter((n) => n.nB <= yTopLocal).map((n) => n.nB));
  const nbBelowTop = Math.min(rect.height, ...neighbors.filter((n) => n.nT >= yBotLocal).map((n) => n.nT));

  const upRoom = yTopLocal - nbAboveBottom - GAP;
  const dnRoom = nbBelowTop - yBotLocal - GAP;

  let topLocal;
  if (upRoom >= TBH) {
    topLocal = yTopLocal - TBH - GAP;
  } else if (dnRoom >= TBH) {
    topLocal = yBotLocal + GAP;
  } else if (spaceAboveLayer >= TBH + GAP) {
    topLocal = nbAboveBottom + GAP;
    if (topLocal + TBH > yTopLocal - GAP) topLocal = Math.max(-TBH - GAP, yTopLocal - TBH - GAP);
  } else {
    topLocal = -TBH - GAP;
  }
  void spaceBelowLayer;

  // Portal: viewport koordinatına çevir.
  const left = rect.left + leftLocal + window.scrollX;
  const top  = rect.top + topLocal + window.scrollY;

  const style = { position: "fixed", left, top, width: TBW, zIndex: 1200 };
  const fam = layer.font_family || "inter";
  const btn = (on) =>
    `h-7 w-7 rounded text-[11px] font-bold ${on ? "bg-slate-900 text-white" : "bg-white text-stone-700 hover:bg-stone-100"}`;
  const sizePx = Math.round(layer.size * rect.height);

  const node = (
    <div style={style}
         className="flex items-center gap-1 bg-white rounded-lg shadow-xl border border-stone-200 px-1.5 py-1"
         onPointerDown={(e) => e.stopPropagation()}
         data-pe-interactive="1"
         data-testid="pe-inline-toolbar">
      <select value={fam} onChange={(e) => onChange(layer.id, { font_family: e.target.value })}
              className="h-7 text-[10px] border rounded px-1 bg-white font-semibold max-w-[110px]"
              style={{ fontFamily: FONT_CSS_MAP[fam] }}
              data-testid="pe-tb-font">
        {FONT_FAMILIES.map(([v, lb]) => (
          <option key={v} value={v} style={{ fontFamily: FONT_CSS_MAP[v] }}>{lb}</option>
        ))}
      </select>
      <span className="w-px h-5 bg-stone-200" />
      <button type="button" onClick={() => onChange(layer.id, { bold: !layer.bold })}
              className={btn(!!layer.bold)}
              style={{ fontWeight: 800 }}
              data-testid="pe-tb-bold" title="Kalın (Bold)">B</button>
      <button type="button" onClick={() => onChange(layer.id, { italic: !layer.italic })}
              className={btn(!!layer.italic)}
              style={{ fontStyle: "italic" }}
              data-testid="pe-tb-italic" title="İtalik">I</button>
      <button type="button" onClick={() => onChange(layer.id, { underline: !layer.underline })}
              className={btn(!!layer.underline)}
              style={{ textDecoration: "underline" }}
              data-testid="pe-tb-underline" title="Altı çizili">U</button>
      <span className="w-px h-5 bg-stone-200" />
      <button type="button" onClick={() => onChange(layer.id, { size: Math.max(0.02, layer.size - 0.005) })}
              className="h-7 w-7 rounded hover:bg-stone-100 text-stone-600 font-bold text-xs"
              data-testid="pe-tb-size-down" title="Küçült">A-</button>
      <span className="text-[10px] text-stone-500 min-w-[36px] text-center font-mono"
            title="Yaklaşık piksel boyutu">
        {sizePx}px
      </span>
      <button type="button" onClick={() => onChange(layer.id, { size: Math.min(0.25, layer.size + 0.005) })}
              className="h-7 w-7 rounded hover:bg-stone-100 text-stone-600 font-bold text-xs"
              data-testid="pe-tb-size-up" title="Büyüt">A+</button>
      <span className="w-px h-5 bg-stone-200" />
      <ColorPopover layer={layer} onChange={onChange} />
      <span className="w-px h-5 bg-stone-200" />
      <div className="flex rounded border border-stone-200 overflow-hidden">
        {[
          ["left",   AlignLeft,   "Sol"],
          ["center", AlignCenter, "Orta"],
          ["right",  AlignRight,  "Sağ"],
        ].map(([a, Icon, lbl]) => (
          <button key={a} type="button" onClick={() => onChange(layer.id, { align: a })}
                  className={`h-7 w-7 inline-flex items-center justify-center ${layer.align === a ? "bg-indigo-600 text-white" : "bg-white text-stone-600 hover:bg-stone-50"}`}
                  data-testid={`pe-tb-align-${a}`} title={lbl}>
            <Icon size={13} />
          </button>
        ))}
      </div>
      <button type="button" onClick={() => onChange(layer.id, { shadow: !layer.shadow })}
              className={`h-7 w-7 rounded text-[10px] ${layer.shadow ? "bg-slate-800 text-white" : "bg-stone-100 text-stone-500 hover:bg-stone-200"}`}
              data-testid="pe-tb-shadow" title="Gölge">S</button>
      <button type="button" onClick={() => onDelete(layer.id)}
              className="h-7 w-7 rounded hover:bg-red-50 text-red-600 ml-auto"
              data-testid="pe-tb-delete" title="Sil">
        <Trash2 size={11} />
      </button>
    </div>
  );
  return createPortal(node, document.body);
}
